import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportStatus } from '../../types';
import './StatusFlowTooltip.css';

interface StatusFlowTooltipProps {
  currentStatus: ReportStatus | string | undefined;
}

const STATUS_FLOW: ReportStatus[] = [
  ReportStatus.NEW,
  ReportStatus.READY,
  ReportStatus.IN_PROGRESS,
  ReportStatus.SIGNED,
  ReportStatus.CLOSED
];

export const StatusFlowTooltip: React.FC<StatusFlowTooltipProps> = ({ currentStatus }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  const getCurrentStatusIndex = (): number => {
    return STATUS_FLOW.findIndex(status => status === currentStatus);
  };

  const currentIndex = getCurrentStatusIndex();

  return (
    <div className="status-flow-tooltip-container">
      <button
        ref={buttonRef}
        type="button"
        className="status-flow-trigger"
        onClick={() => setIsVisible(!isVisible)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        aria-label={t('statusFlow.helpLabel')}
        aria-expanded={isVisible}
      >
        <span className="material-icons">help_outline</span>
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="status-flow-tooltip"
          role="tooltip"
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <div className="tooltip-header">
            {t('statusFlow.title')}
          </div>
          <div className="tooltip-content">
            <div className="status-flow">
              {STATUS_FLOW.map((status, index) => {
                const isCurrent = status === currentStatus;
                const isPast = index < currentIndex;
                const isFuture = index > currentIndex;

                return (
                  <React.Fragment key={status}>
                    <div
                      className={`status-step ${isCurrent ? 'current' : ''} ${isPast ? 'past' : ''} ${isFuture ? 'future' : ''}`}
                    >
                      <span className="status-name">{t(`status.${status}`)}</span>
                    </div>
                    {index < STATUS_FLOW.length - 1 && (
                      <span className={`status-arrow ${isPast ? 'past' : ''}`}>
                        <span className="material-icons">arrow_forward</span>
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {currentStatus && currentStatus !== ReportStatus.CANCELED && (
              <div className="current-status-info">
                <div className="status-label">
                  {t('statusFlow.currentStatus')}: <strong>{t(`status.${currentStatus}`)}</strong>
                </div>
                <div className="status-description">
                  {t(`statusFlow.description.${currentStatus}`)}
                </div>
              </div>
            )}
            {currentStatus === ReportStatus.CANCELED && (
              <div className="current-status-info canceled">
                <div className="status-label">
                  {t('statusFlow.currentStatus')}: <strong>{t('status.CANCELED')}</strong>
                </div>
                <div className="status-description">
                  {t('statusFlow.description.CANCELED')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusFlowTooltip;
